import { useState, useEffect } from 'react';

import Input from '../../../shared/components/Input';
import PhoneInput from '../../../shared/components/PhoneInput';
import { LABEL_STYLE } from '../../../shared/constants/styleConstants';
import toolTipConstants from '../../../shared/constants/toolTipConstants';
import { useMediaQuery } from '../../../shared/hooks/useMediaQuery';

const YourDetails = ({ onDataChange, initialData, onValidityChange, addressData, markPhoneTouched, onInteraction }) => {
    const isResponsiveView = useMediaQuery('(max-width: 1024px)');

    const [yourDetailsData, setYourDetailsData] = useState({
        companyName: initialData?.companyName || initialData?.company || '',
        phone: initialData?.phone || initialData?.phoneNumber || '',
        poNumber: initialData?.poNumber || initialData?.purchaseOrderNumber || '',
        taxNumber: initialData?.taxNumber || ''
    });
    const [touched, setTouched] = useState({});

    // Mark phone as touched when address is populated OR whenever flagged by parent
    useEffect(() => {
        if ((addressData && (addressData.line1 || addressData.city || addressData.postcode || addressData.zip)) || markPhoneTouched) {
            setTouched(prev => ({ ...prev, phone: true }));
        }
    }, [addressData, markPhoneTouched]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange(yourDetailsData);
        }

        // Validate mandatory phone number
        // Check if phone exists and has content (ignoring dial code if possible, but basic check is length > 0)
        // PhoneInput generally manages formatting, but here we just ensure a value is present.
        const isPhoneValid = yourDetailsData.phone && yourDetailsData.phone.trim().length > 0;

        if (onValidityChange) {
            onValidityChange(isPhoneValid);
        }
    }, [yourDetailsData, onDataChange, onValidityChange]);

    const handleChange = (field, value) => {
        setYourDetailsData(prev => ({ ...prev, [field]: value }));
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };



    return (
        <div className="w-full">
            <h2 className="font-noto font-semibold text-[18px] text-[var(--Neutral_grey-700,#344054)] mb-[26px]">Your details</h2>

            {/* Company Name */}
            <div className="mb-3">
                <Input
                    label="Company name"
                    labelClassName={LABEL_STYLE}
                    name="companyName"
                    value={yourDetailsData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    onBlur={() => handleBlur('companyName')}
                    tooltipContent={toolTipConstants.COMPANY_NAME}
                />
            </div>

            {/* Phone & PO Number Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3">
                <PhoneInput
                    label="Phone number"
                    labelClassName={LABEL_STYLE}
                    name="phone"
                    value={yourDetailsData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    onBlur={() => handleBlur('phone')}
                    hasError={touched.phone && (!yourDetailsData.phone || yourDetailsData.phone.trim().length === 0)}
                    error={touched.phone && (!yourDetailsData.phone || yourDetailsData.phone.trim().length === 0) ? 'Phone number is required' : ''}
                    required
                />
                <Input
                    label="Purchase order number"
                    labelClassName={LABEL_STYLE}
                    name="poNumber"
                    value={yourDetailsData.poNumber}
                    onChange={(e) => handleChange('poNumber', e.target.value)}
                    optional
                />
            </div>

            {/* Tax Details Section */}
            <div className="bg-[#F9FAFB] rounded-[6px] px-6 py-6 md:py-0 flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 md:gap-6 h-[210px] md:h-[118px]">
                <div className="w-full md:w-1/2">
                    <h3 className="font-noto font-semibold text-[16px] text-[var(--Neutral_grey-700,#344054)] leading-[24px] mb-[4px]">Tax details</h3>
                    <div className="flex items-center">
                        <p className="font-noto font-normal text-[12px] text-[#667085] leading-[16px]">
                            This information is only required if you need a VAT invoice with your purchase.
                        </p>
                    </div>
                </div>
                <div className="w-full md:w-1/2">
                    <Input
                        label="Tax number"
                        labelClassName={LABEL_STYLE}
                        name="taxNumber"
                        value={yourDetailsData.taxNumber}
                        onChange={(e) => handleChange('taxNumber', e.target.value)}
                        optional
                        className="mb-0"
                    />
                </div>
            </div>
        </div>
    );
};

export default YourDetails;
